import { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, Send, Compass } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { mapsHref } from "@/components/layout/Footer";

export default function Contact() {
  const { settings } = useSettings();
  const [f, setF] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.email || !f.message) { toast.error("Champs requis manquants"); return; }
    setLoading(true);
    try {
      await api.post("/contact", f);
      toast.success("Message envoyé ! Nous vous répondrons rapidement.");
      setF({ name: "", email: "", subject: "", message: "" });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
        <div>
          <h1 className="font-display font-extrabold text-3xl text-slate-dark mb-2">Contactez-nous</h1>
          <p className="text-slate-500">Une question, un conseil ? Notre équipe est à votre écoute 7j/7.</p>
        </div>
        <Link to="/visite-360" data-testid="virtual-tour-link" className="flex items-center gap-2 px-5 py-3 rounded-full bg-gradient-to-r from-mint-600 to-mint-500 text-white font-semibold text-sm shadow-lg shadow-mint-600/30 hover:shadow-xl transition-shadow">
          <Compass className="w-4 h-4" /> Explorez à 360° notre boutique
        </Link>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        <div className="space-y-4">
          {[[MapPin, "Adresse", settings.address, true], [Phone, "Téléphone", settings.phone, false], [Mail, "Email", settings.email, false], [Clock, "Horaires", settings.horaires, false]].map(([Icon, t, v, isAddr], i) => (
            <div key={i} className="flex items-start gap-4 bg-white rounded-2xl border border-slate-200/80 p-5">
              <span className="w-11 h-11 rounded-xl bg-mint-50 grid place-items-center shrink-0"><Icon className="w-5 h-5 text-mint-600" /></span>
              <div><div className="font-display font-bold text-sm">{t}</div>
                {isAddr
                  ? <a href={mapsHref(settings)} target="_blank" rel="noopener noreferrer" data-testid="contact-address-map" className="text-mint-700 hover:underline text-sm font-medium">{v} <span className="text-xs text-slate-400">(voir sur Google Maps)</span></a>
                  : <div className="text-slate-600 text-sm">{v}</div>}
              </div>
            </div>
          ))}
          <div className="rounded-2xl overflow-hidden border border-slate-200/80 h-64" data-testid="contact-map">
            <iframe title="Localisation L'olivier" width="100%" height="100%" style={{ border: 0 }} loading="lazy"
              src={`https://www.google.com/maps?q=${encodeURIComponent(settings.address || "Alger, Algérie")}&output=embed`} />
          </div>
        </div>

        <form onSubmit={submit} className="bg-white rounded-2xl border border-slate-200/80 p-6 space-y-4 h-fit">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Nom *" value={f.name} onChange={set("name")} testid="contact-name" />
            <Field label="Email *" type="email" value={f.email} onChange={set("email")} testid="contact-email" />
          </div>
          <Field label="Sujet" value={f.subject} onChange={set("subject")} testid="contact-subject" />
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1.5">Message *</label>
            <textarea rows={5} value={f.message} onChange={set("message")} data-testid="contact-message"
              className="w-full px-4 py-2.5 rounded-xl border border-mint-200 outline-none focus:ring-2 focus:ring-mint-500" />
          </div>
          <button type="submit" disabled={loading} data-testid="contact-submit"
            className="flex items-center gap-2 px-6 py-3 rounded-full bg-mint-600 hover:bg-mint-700 text-white font-semibold shadow-lg shadow-mint-600/30 disabled:opacity-50">
            <Send className="w-4 h-4" /> {loading ? "Envoi…" : "Envoyer"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, testid, ...props }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-600 mb-1.5">{label}</label>
      <input {...props} data-testid={testid} className="w-full px-4 py-2.5 rounded-xl border border-mint-200 outline-none focus:ring-2 focus:ring-mint-500" />
    </div>
  );
}
